import { useSelector } from 'react-redux';
import { t } from 'ttag';

import { getLicenseType } from 'common/state/organization';

import { useContracts } from '@domains/Organization/DataUsage/PeopleCredit/queries';
import { useShouldShowFeatureQuery } from '@domains/Organization/queries';
import { LICENSE_TYPE_USER_CREDIT } from '@domains/Organization/service';

import usePermission, {
  UPDATE_ORGANIZATION,
  READ_API_KEYS,
  READ_DATA_USAGE,
  READ_PCL_USAGE,
  READ_EVENTS,
  READ_ORGANIZERS,
  READ_SSO_CONNECTIONS,
  READ_SENDER_EMAIL_DOMAINS,
  READ_PAYMENT_GATEWAY,
  READ_CUSTOM_DOMAINS,
  READ_MULTI_EVENT_APPS,
} from './usePermission';

export const TAB_ADMIN = 'admin';
export const TAB_APPS = 'apps';
export const TAB_EVENTS = 'events';
export const TAB_API_KEYS = 'api-keys';
export const TAB_ORGANIZERS = 'organizers';
export const TAB_SSO = 'sso';
export const TAB_EMAIL_DOMAIN = 'email-domain';
export const TAB_PAYMENT_GATEWAYS = 'payment-gateways';
export const TAB_DATA_USAGE = 'data-usage';
export const TAB_PEOPLE_CREDIT = 'people-credit';
export const TAB_VIDEO_USAGE = 'video-usage';
export const TAB_CUSTOM_DOMAIN = 'custom-domain';

export const VALID_TABS = [
  TAB_ADMIN,
  TAB_APPS,
  TAB_EVENTS,
  TAB_DATA_USAGE,
  TAB_ORGANIZERS,
  TAB_API_KEYS,
  TAB_SSO,
  TAB_EMAIL_DOMAIN,
  TAB_PAYMENT_GATEWAYS,
  TAB_PEOPLE_CREDIT,
  TAB_VIDEO_USAGE,
  TAB_CUSTOM_DOMAIN,
];

/**
 * Hook that returns the list of organization tabs/items with feature flags, configs, and permissions.
 * This hook ensures the dropdown in the header and the tabs on the Organization Details page
 * are always in sync.
 *
 * @param {string} organizationId - The organization ID
 * @returns {Object} - { tabs, isLoading, contracts }
 */
const useOrganizationTabs = organizationId => {
  const licenseType = useSelector(getLicenseType);
  const { loading: isLoadingAuth, authorized } = usePermission();

  const hasOrganizationId = !!organizationId;

  // Feature flag queries
  const { data: showEmailDomainTab, isLoading: isShowEmailDomainsLoading } =
    useShouldShowFeatureQuery(organizationId, 'is_whitelabel_sender_email_domain_enabled', {
      enabled: hasOrganizationId && authorized(READ_SENDER_EMAIL_DOMAINS),
    });

  const { data: showSSOTab, isLoading: isShowSSOLoading } = useShouldShowFeatureQuery(
    organizationId,
    'is_sso_enabled',
    {
      enabled: hasOrganizationId && authorized(READ_SSO_CONNECTIONS),
    },
  );

  const { data: showPaymentGatewaysTab, isLoading: isShowPaymentGatewaysTabLoading } =
    useShouldShowFeatureQuery(organizationId, 'reg_payments', {
      enabled: hasOrganizationId,
    });

  const { data: showCustomDomainTab, isLoading: isShowCustomDomainTabLoading } =
    useShouldShowFeatureQuery(organizationId, 'is_custom_domain_enabled', {
      enabled: hasOrganizationId,
    });

  const { data: contracts = [], isLoading: isContractsLoading } = useContracts(
    organizationId,
    undefined,
    hasOrganizationId && authorized(READ_PCL_USAGE),
  );

  // Show people credits even if license type is event credit but they have contracts
  const renderPeopleCredits =
    !isContractsLoading && (contracts?.length > 0 || licenseType === LICENSE_TYPE_USER_CREDIT);

  // When there's no organizationId, all queries are disabled, so we're not loading
  const isLoading =
    hasOrganizationId &&
    ((isShowSSOLoading && authorized(READ_SSO_CONNECTIONS)) ||
      (isShowEmailDomainsLoading && authorized(READ_SENDER_EMAIL_DOMAINS)) ||
      isShowPaymentGatewaysTabLoading ||
      isShowCustomDomainTabLoading ||
      (isContractsLoading && authorized(READ_PCL_USAGE)) ||
      isLoadingAuth);

  // Build the tabs list - order matters!
  const tabs = [
    authorized(READ_EVENTS) && {
      label: t`Events`,
      key: TAB_EVENTS,
    },
    authorized(READ_ORGANIZERS) && {
      label: t`Organizers`,
      key: TAB_ORGANIZERS,
    },
    authorized(READ_PCL_USAGE) &&
      renderPeopleCredits && {
        label: t`People Credit`,
        key: TAB_PEOPLE_CREDIT,
      },
    authorized(READ_DATA_USAGE) && {
      label: t`Video Usage`,
      key: TAB_VIDEO_USAGE,
    },
    authorized(READ_API_KEYS) && {
      label: t`API Keys`,
      key: TAB_API_KEYS,
    },
    authorized(READ_SSO_CONNECTIONS) &&
      showSSOTab && {
        label: t`SSO Connections`,
        key: TAB_SSO,
      },
    authorized(READ_SENDER_EMAIL_DOMAINS) &&
      showEmailDomainTab && {
        label: t`Email Domains`,
        key: TAB_EMAIL_DOMAIN,
      },
    authorized(READ_PAYMENT_GATEWAY) &&
      showPaymentGatewaysTab && {
        label: t`Payment Gateways`,
        key: TAB_PAYMENT_GATEWAYS,
      },
    authorized(READ_CUSTOM_DOMAINS) &&
      showCustomDomainTab && {
        label: t`Custom Domain`,
        key: TAB_CUSTOM_DOMAIN,
      },
    authorized(READ_MULTI_EVENT_APPS) && {
      label: t`Apps`,
      key: TAB_APPS,
    },
    authorized(UPDATE_ORGANIZATION) && {
      label: t`Admin`,
      key: TAB_ADMIN,
      color: '#f5222d', // Staff-only tab color
    },
  ].filter(Boolean);

  return {
    tabs,
    isLoading,
  };
};

export default useOrganizationTabs;
